// Outback Station — stage surface materials (texlib layers), on this stage's three reserved PATTERN slots.
//
// SURF maps your names to the slot ids; use them in layout.js as `pattern: SURF.<name>`. SURFACES lists what each
// slot holds: { slot, name, onWall?, onTop?, mat }.
//   redearth     sun-baked red-ochre outback dirt & claypan: polygonal mud-crack network, scattered dark ironstone
//                pebbles, wind-blown fine sand ripples in the hollows. Paint mask: block colour tints the warm ochre
//                clay while ironstone pebbles and deep desiccation cracks keep their natural character; walls → sanddune.
//   sanddune     wind-rippled red desert sand & layered Flinders sandstone: asymmetric wind ripples on top faces and
//                stratified sandstone bedding lines on vertical bluffs/escarpments.
//   outbackwood  sun-bleached jarrah / ironbark station timber decking & sleepers: 20 cm rough-sawn planks, staggered
//                butt joints, weathered silver-grey & reddish heartwood grain, sunken iron bolt heads.
export const SURF = { redearth: 49, sanddune: 50, outbackwood: 51 };

const redearth = {
  detail: 0.55, scale: 3.2, tint: true, mask: true, alpha: false, mode: 1, sym: 7, hr: [-0.005, 0.003], ao: 0.4,
  prep: `f[0] = FB(uv, ivec2(3), 4, 0.55, 4901u); f[1] = FB(uv, ivec2(14), 3, 0.5, 4903u); f[2] = FB(uv, ivec2(56), 2, 0.5, 4907u);
  f[3] = FB(uv, ivec2(24, 6), 3, 0.5, 4909u);
  vec2 wq = uv + 0.014 * vec2(sin(TAU * (4.0 * uv.y + 2.0 * uv.x)), cos(TAU * (3.0 * uv.x - 4.0 * uv.y)));
  w[0] = WO(wq, ivec2(9), 0.85, 4911u); w[1] = WO(uv, ivec2(32), 0.92, 4913u);`,
  surf: /* glsl */`
  // sun-baked claypan polygons (c[0]) with fine desiccation cracks + scattered ironstone gravel (c[1])
  vec4 poly = c[0];
  float pEdge = poly.y - poly.x;
  float crack = (1.0 - smoothstep(0.0, 0.055, pEdge)) * smoothstep(-0.25, 0.45, n[0]);
  float dish = smoothstep(0.0, 0.45, pEdge);
  // wind-blown sand ripples pooling in lower hollows
  float ripPhase = TAU * (P.x * 2.5 + P.y * 0.75) + 1.6 * n[1];
  float rip = 0.5 + 0.5 * sin(ripPhase + 0.35 * sin(ripPhase));
  float sandPool = smoothstep(-0.05, 0.55, n[0] + 0.25 * n[3]);
  // ironstone pebbles scattered across the crust
  vec4 peb = c[1];
  float pe = peb.y - peb.x;
  float hasPeb = step(0.68, fract(peb.z * 11.7 + peb.w * 3.1)) * smoothstep(-0.2, 0.5, n[1]);
  float pMask = hasPeb * smoothstep(0.04, 0.16, pe);
  float pDome = sqrt(clamp(pe / 0.4, 0.0, 1.0));
  // claypan & sand modulation (paint-masked so block colour drives the warm ochre/terracotta tone)
  float earthMod = 0.82 * (1.0 + 0.08 * n[0] + 0.05 * n[1] + 0.03 * n[2] + 0.04 * (rip - 0.5) * sandPool);
  earthMod *= 1.0 + 0.03 * (fract(poly.z * 7.13) - 0.5);
  vec3 ironCol = mix(lin(vec3(0.32, 0.18, 0.13)), lin(vec3(0.46, 0.26, 0.18)), fract(peb.z * 5.3)) * (0.9 + 0.2 * pDome);
  vec3 crackCol = lin(vec3(0.28, 0.15, 0.10)) * (0.9 + 0.1 * n[2]);
  float nonTint = clamp(pMask + crack * 0.75, 0.0, 1.0);
  s.alb = mix(crackCol, ironCol, pMask / max(nonTint, 1e-4)) * nonTint;
  s.a = (1.0 - nonTint) * earthMod;
  s.h = 0.0008 * dish - 0.0032 * crack + 0.0009 * rip * sandPool + 0.0024 * pDome * pMask + 0.0002 * n[2];
  s.rough = mix(0.91 - 0.05 * sandPool + 0.04 * crack, 0.68, pMask);
  s.cav = 1.0 - 0.38 * crack - 0.14 * pMask * (1.0 - pDome);`,
};

const sanddune = {
  detail: 0.5, scale: 2.8, tint: true, mask: true, alpha: false, mode: 1, sym: 3, hr: [-0.004, 0.004], ao: 0.35,
  prep: `f[0] = FB(uv, ivec2(3), 4, 0.55, 5001u); f[1] = FB(uv, ivec2(10, 4), 3, 0.5, 5003u);
  f[2] = FB(uv, ivec2(28, 6), 3, 0.5, 5007u); f[3] = FB(uv, ivec2(84), 2, 0.5, 5009u);
  w[0] = WO(uv, ivec2(18, 8), 0.85, 5011u); w[1] = WO(uv, ivec2(48), 0.9, 5013u);`,
  surf: /* glsl */`
  // wind-blown asymmetric sand ripples + stratified Flinders sandstone bedding lines
  float w1 = TAU * (uv.y * 6.0 + uv.x * 1.0 + 0.18 * n[0] + 0.08 * n[1]);
  float crest = 0.5 + 0.5 * sin(w1 - 0.42 * cos(w1));
  float strata = 0.5 + 0.5 * sin(TAU * (uv.y * 3.0 + 0.12 * n[0]) + 1.4 * n[1]);
  float ledge = smoothstep(0.32, 0.72, strata) * (0.6 + 0.4 * n[2]);
  // small sandstone / quartz granules along ripple troughs
  vec4 grn = c[1];
  float gMask = step(0.82, fract(grn.z * 9.7)) * smoothstep(0.06, 0.18, grn.y - grn.x) * (1.0 - crest);
  float spark = smoothstep(0.45, 0.85, n[3]);
  float sandMod = 0.84 * (1.0 + 0.07 * (crest - 0.5) + 0.06 * (ledge - 0.3) + 0.05 * n[0] + 0.03 * n[3]);
  vec3 quartz = mix(lin(vec3(0.84, 0.64, 0.46)), lin(vec3(0.48, 0.25, 0.16)), fract(grn.z * 4.1));
  s.alb = quartz * gMask;
  s.a = (1.0 - gMask * 0.75) * sandMod;
  s.h = 0.0018 * (crest - 0.5) + 0.0014 * ledge + 0.0012 * gMask + 0.00025 * n[3];
  s.rough = 0.88 - 0.06 * crest - 0.05 * spark + 0.04 * gMask;
  s.cav = 1.0 - 0.12 * (1.0 - crest) - 0.1 * (1.0 - strata);`,
};

const outbackwood = {
  detail: 0.5, scale: 2.4, tint: true, alpha: false, mode: 1, sym: 3, hr: [-0.006, 0.002], ao: 0.5,
  prep: `ivec2 pc = wrp(ivec2(floor(P.x / 1.2), floor(P.y / 0.2)), ivec2(2, 12));
  uint ps = uint(pc.x * 12 + pc.y) * 89u;
  f[0] = FB(uv, ivec2(2, 24), 3, 0.5, 5101u + ps); f[1] = FB(uv, ivec2(4, 64), 3, 0.5, 5103u + ps);
  f[2] = FB(uv, ivec2(16, 96), 2, 0.5, 5107u); w[0] = WO(uv, ivec2(8, 24), 0.75, 5111u);`,
  surf: /* glsl */`
  // 20 cm rough-sawn jarrah / ironbark planks along X, staggered butt joints every 1.2 m
  const float PW = 0.2, PL = 1.2;
  int row = int(floor(P.y / PW));
  int rowW = wrp(ivec2(row, 0), ivec2(12)).x;
  float stag = (row & 1) == 0 ? 0.0 : 0.6;
  float px = P.x + stag;
  int col = int(floor(px / PL));
  ivec2 id = wrp(ivec2(col, rowW), ivec2(2, 12));
  float dy = jd(P.y, PW);
  float dx = jd(px, PL);
  vec2 pr = edgeProf(min(dy, dx), 0.0035, 0.009, 0.0028, 0.005);
  float gap = pr.y;
  // sun-bleached silver-grey outer fibres mixed with warm reddish-brown jarrah heartwood
  float hid = hf(id, 5u), hid2 = hf(id, 13u);
  float grain = 0.6 * n[0] + 0.4 * n[1] + 0.2 * sin(TAU * (P.y * 18.0 + 0.35 * n[0]));
  float bleach = smoothstep(-0.15, 0.55, n[0] + 0.3 * (hid2 - 0.5));
  vec3 jarrah = mix(lin(vec3(0.66, 0.46, 0.34)), lin(vec3(0.76, 0.58, 0.43)), hid);
  vec3 silver = lin(vec3(0.74, 0.70, 0.63));
  vec3 wood = mix(jarrah, silver, 0.38 * bleach) * (0.92 + 0.16 * hid2) * (1.0 + 0.11 * grain + 0.04 * n[2]);
  // sunken rusty bolt heads near plank butt ends
  float endX = min(fract(px / PL), 1.0 - fract(px / PL)) * PL;
  float py = (fract(P.y / PW) - 0.5) * PW;
  float bd = min(length(vec2(endX - 0.045, abs(py) - 0.055)), length(vec2(endX - 0.045, py)));
  float bolt = (1.0 - aa(0.008, bd)) * (1.0 - gap);
  vec3 seamCol = lin(vec3(0.28, 0.19, 0.14)) * (1.0 + 0.08 * n[2]);
  vec3 colOut = mix(wood, lin(vec3(0.24, 0.18, 0.15)), bolt * 0.85);
  s.alb = mix(colOut, seamCol, gap);
  s.h = pr.x + (0.00045 * grain + 0.00015 * n[2] - 0.0012 * bolt) * (1.0 - gap);
  s.rough = mix(0.84 - 0.06 * bleach - 0.2 * bolt, 0.95, gap);
  s.metal = 0.55 * bolt * (1.0 - gap);
  s.cav = mix(1.0 - 0.22 * bolt, 0.42, gap);`,
};

export const SURFACES = [
  { slot: 49, name: 'redearth', onWall: 50, mat: redearth },
  { slot: 50, name: 'sanddune', mat: sanddune },
  { slot: 51, name: 'outbackwood', mat: outbackwood },
];
